import React from 'react';
import { isNull } from 'lodash';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { DefaultFilledButton, SecondaryButton } from '@magoosh/lib/button';
import DonutChart from '@magoosh/lib/charts/donut_chart/simple';
import { SquareIcon } from '@magoosh/lib/icons';
import { paths } from 'config/path_helpers';
import { pluralize } from 'utilities/pluralize';
import { secondsFormattedAsMinutes } from 'utilities/time';

import ContextualLink from '@test_prep/app/components/contextual_link';
import { useAppContext } from '@test_prep/app/context';

import { ExamSection } from '../../types';

import colors from '@magoosh/lib/styles/colors.module.scss';

export default (props: ExamSection) => {
  const { branding, features } = useAppContext().data;

  // There is similar code in the analytics subject header component. Would be nice to extract into
  // something resuable.
  const iconForSection = () => {
    const colorPalette = { dark: colors.purple, light: colors.purpleLight };

    if (['Math', 'Quantitative Reasoning'].indexOf(props.name) !== -1) {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconType="math_operations"
          size={48}
        />
      );
    } else if (['English', 'Reading', 'Reading & Writing', 'Verbal', 'Verbal Reasoning'].indexOf(props.name) !== -1) {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconType="notebook"
          size={48}
        />
      );
    } else if (props.name === 'Data Insights') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconContents="bar-chart"
          iconType="fa"
          size={48}
        />
      );
    } else if (props.name === 'Listening') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconContents="headphones"
          iconType="fa"
          size={48}
        />
      );
    } else if (props.name === 'Science') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconContents="flask"
          iconType="fa"
          size={48}
        />
      );
    } else if (props.name === 'Speaking') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconContents="commenting-o"
          iconType="fa"
          size={48}
        />
      );
    } else if (props.name === 'Writing') {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconContents="edit"
          iconType="fa"
          size={48}
        />
      );
    } else {
      return (
        <SquareIcon
          colorPalette={colorPalette}
          iconContents={props.name[0]}
          iconType="letter"
          size={48} />
      );
    }
  };

  return (
    <div className="d-flex flex-column gap-m">
      <div className="align-items-lg-center d-flex flex-column flex-lg-row gap-s">
        <div className="align-items-center d-flex gap-s">
          {iconForSection()}
          <h3 className="u-margin-A-n">{props.name}</h3>
        </div>

        {
          props.scoreEstimate && (
            <div className="d-flex flex-1 gap-xs justify-content-lg-end small">
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`tooltip-estimated-score-${props.id}`}>
                    We provide a score estimate after you answer {pluralize(props.scoreEstimate.answerCountThreshold, `${props.name} question`)}, based on actual {branding.examName} scores from previous Magoosh students. The more questions you answer, the more accurate your estimate will be. (Only your most recent answer for each question is used.)
                  </Tooltip>
                }
              >
                <div className="text-underdash">Estimated Score:</div>
              </OverlayTrigger>

              <div>
                {
                  props.scoreEstimate.low && props.scoreEstimate.high ? (
                    `${props.scoreEstimate.low} - ${props.scoreEstimate.high}`
                  ) : 'Unknown'
                }
              </div>
            </div>
          )
        }
      </div>

      {
        props.groups.map((group, idx) => (
          <div key={idx} className="d-flex flex-column gap-m">
            {
              (group.name !== props.name || group.performance.type === 'value') && (
                <div className="d-flex flex-column gap-xxs">
                  {group.name !== props.name && <h4 className="u-margin-A-n">{group.name}</h4>}
                  {
                    group.performance.type === 'value' && (
                      <div className="small">
                        Scores range from {group.performance.range.low} to {group.performance.range.high}
                      </div>
                    )
                  }
                </div>
              )
            }

            <div className="align-items-lg-center d-flex flex-column flex-lg-row gap-m gap-lg-l">
              <div className="align-items-center d-flex flex-1 gap-m">
                {/*TODO - match color palette of analytics*/}
                <div className="flex-none">
                  <DonutChart
                    colorPalette={{ dark: colors.dataDarkPurple, light: colors.dataMutedPurple }}
                    data={
                      group.performance.value && group.performance.type === 'value' ? (
                        (group.performance.value / group.performance.range.high) * 100
                      ) : group.performance.value
                    }
                    interior={
                      group.performance.type === 'value' ? (
                        <strong>
                          {
                            isNull(group.performance.value) ? (
                              <span style={{ color: colors.dataDarkPurple, fontSize: 20 }}>-</span>
                            ) : (
                              <span style={{ color: colors.dataDarkPurple, fontSize: 25 }}>
                                {group.performance.value}
                              </span>
                            )
                          }
                        </strong>
                      ) : null
                    }
                    size={100}
                  />
                </div>

                <table className="d-flex flex-none">
                  <tbody>
                    <tr>
                      <td style={{ padding: 0, paddingRight: 10 }}>
                        <span className="hidden-xs">Questions Answered:</span>
                        <span className="visible-xs">Answered:</span>
                      </td>
                      <td style={{ padding: 0 }}>{group.questions.answered}</td>
                    </tr>

                    <tr>
                      <td style={{ padding: 0, paddingRight: 10 }}>Your Pace:</td>
                      <td style={{ padding: 0 }}>{secondsFormattedAsMinutes(group.questions.paceInSeconds.user, false)}</td>
                    </tr>

                    <tr>
                      <td style={{ padding: 0, paddingRight: 10 }}>Others' Pace:</td>
                      <td style={{ padding: 0 }}>{secondsFormattedAsMinutes(group.questions.paceInSeconds.others, false)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="visible-lg" style={{ background: '#EEEEEF', height: 100, width: 1 }} />

              <div className="d-flex flex-column gap-xs">
                <div className="d-flex flex-1 gap-s">
                  <ContextualLink
                    className="flex-1 text-hover-no-decoration text-no-decoration"
                    href={paths.startQuickPractice({
                      'practice_session[exam_section_id]': props.id,
                      'practice_session[tag_ids]': group.subjectTagIds.join(','),
                    })}
                  >
                    <SecondaryButton block size="sm">
                      Quick Practice
                    </SecondaryButton>
                  </ContextualLink>

                  <ContextualLink
                    className="flex-1 text-hover-no-decoration text-no-decoration"
                    href={paths.startPractice({
                      'practice_session[exam_section_id]': props.id,
                      'practice_session[tag_ids]': group.subjectTagIds.join(',')
                    })}
                  >
                    <SecondaryButton block size="sm">
                      Custom Practice
                    </SecondaryButton>
                  </ContextualLink>
                </div>

                <div className="visible-lg">
                  <div className="d-flex justify-content-center text-gray-light" style={{ fontSize: 12 }}>
                    {pluralize(group.questions.remaining, 'practice question')} left
                  </div>
                </div>
              </div>
            </div>

            {idx !== props.groups.length - 1 && <hr className="u-margin-A-n" />}
          </div>
        ))
      }

      <hr className="u-margin-A-n" />

      <div className="d-flex gap-s">
        <ContextualLink href={paths.lessons()}>
          <DefaultFilledButton size="sm">
            <span className="visible-xs">Lessons</span>
            <span className="hidden-xs">View Lessons</span>
          </DefaultFilledButton>
        </ContextualLink>

        {
          features.analytics && (
            <ContextualLink href={paths.analytics({ sections: props.slug })}>
              <DefaultFilledButton size="sm">
                <span className="visible-xs">Analytics</span>
                <span className="hidden-xs">View Analytics</span>
              </DefaultFilledButton>
            </ContextualLink>
          )
        }

        <ContextualLink href={paths.examSectionNotes(props.slug)}>
          <DefaultFilledButton size="sm">
            My Notes ({props.notes})
          </DefaultFilledButton>
        </ContextualLink>
      </div>
    </div>
  );
};
